import { userStorage } from '~/shared/storage'

import { isReauthRequiredError } from './reauthRequiredError'

/**
 * The claims the suite reads off the session's access token: the permissions "Enabled products"
 * filters on, and the tenancy pair the session's identity is resolved from.
 *
 * This is the only place in the suite that decodes a token (Atlas ADR-0022). Both tenancy claims are set
 * by Auth0's post-login action (`action-regard-claims.js` in `iac`) on the access token and the ID
 * token alike; the access token is read here because it is the one the atlas service authorizes against, so
 * the suite and the server resolve tenancy from the same claims.
 */
export interface SessionClaims {
  /** Stable user identifier supplied by either Auth0 or the EMR session. */
  subject: string | null
  /**
   * Every Auth0 permission the access token carries, verbatim, product permissions
   * (`arc:read`, `scout:read`) among them.
   *
   * Read from the `permissions` claim alone, not `scopes`: the `regard-admin` resource server issues
   * user tokens under the `access_token_authz` dialect, and the space-delimited `scopes` form only
   * ever appears on a machine token, which no browser session holds.
   */
  permissions: string[]

  /**
   * Whether this is a Regard admin's session, from the `regard_admin` claim.
   * Only the literal `true` counts.
   */
  isAdmin: boolean

  /**
   * The session's single `customer` claim, or `null` where the token carries none.
   * An empty string reads as absent.
   */
  customer: string | null
}

const NO_CLAIMS: SessionClaims = {
  subject: null,
  permissions: [],
  isAdmin: false,
  customer: null,
}

let currentClaims: SessionClaims = NO_CLAIMS

const listeners = new Set<() => void>()

const notifyListeners = () => {
  for (const listener of listeners) {
    listener()
  }
}

/**
 * Holds the session's decoded claims for the guarded route tree to read synchronously.
 *
 * Mirrors `customerHolder`: written once, by `RequireAuth`, before any guarded route renders, so a
 * consumer can read it during render synchronously.
 *
 * `subscribe` exists for the consumers that render *outside* the guarded tree, which the write cannot
 * be ordered before: the analytics bridges mount beside the router in `App`, so they are already
 * rendered when the resolve lands and nothing else would re-render them. A synchronous read alone
 * would leave them attributing the whole session to an unread token.
 */
export const sessionClaimsHolder = {
  get: (): SessionClaims => currentClaims,
  set: (claims: SessionClaims) => {
    currentClaims = claims
    notifyListeners()
  },
  clear: () => {
    currentClaims = NO_CLAIMS
    notifyListeners()
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

/** Decodes one base64url segment of a JWT into its parsed JSON, throwing on anything unreadable. */
const parseTokenPayload = (token: string): unknown => {
  const payload = token.split('.')[1]
  if (!payload) {
    throw new Error('Access token has no payload segment')
  }

  const base64 = payload.replaceAll('-', '+').replaceAll('_', '/')
  // `atob` rejects a length that is not a multiple of four, and base64url omits the padding.
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  const bytes = Uint8Array.from(
    atob(padded),
    (character) => character.codePointAt(0) ?? 0,
  )

  return JSON.parse(new TextDecoder().decode(bytes))
}

/**
 * Reads the `permissions` claim as the list of grants it should be, or as no grants at all.
 *
 * Non-string entries in an otherwise valid claim are dropped individually: a permission the suite
 * cannot read is one it should not act on, and it says nothing about the entries beside it.
 */
const readPermissions = (claim: unknown): string[] =>
  Array.isArray(claim)
    ? claim.filter(
        (permission): permission is string => typeof permission === 'string',
      )
    : []

/**
 * Reads the claims the suite cares about off an access token.
 *
 * If the token is unreadable in any way (absent, not a JWT, an undecodable payload, a payload that is
 * not an object), it yields no claims rather than throwing. Every claim is then read independently, so
 * one written in a shape the suite does not expect costs only itself: a malformed `permissions` claim
 * withholds products without also making a Regard admin's session look like a customer's.
 *
 * No claim is inferred from another. In particular a token carrying neither tenancy claim yields
 * neither, which `Bootstrap` treats as unauthorized.
 */
export const decodeSessionClaims = (
  token: string | undefined,
): SessionClaims => {
  if (!token) {
    return NO_CLAIMS
  }

  let payload: unknown
  try {
    payload = parseTokenPayload(token)
  } catch {
    return NO_CLAIMS
  }

  if (typeof payload !== 'object' || payload === null) {
    return NO_CLAIMS
  }

  const claims = payload as {
    sub?: unknown
    customer?: unknown
    permissions?: unknown
    regard_admin?: unknown
  }

  return {
    subject:
      typeof claims.sub === 'string' && claims.sub !== '' ? claims.sub : null,
    permissions: readPermissions(claims.permissions),
    isAdmin: claims.regard_admin === true,
    customer:
      typeof claims.customer === 'string' && claims.customer !== ''
        ? claims.customer
        : null,
  }
}

/**
 * Reads the access token, yielding `undefined` for a transient failure and propagating an error that
 * requires interactive login.
 */
const readAccessToken = async (
  getAccessToken: () => Promise<string | undefined>,
): Promise<string | undefined> => {
  try {
    return await getAccessToken()
  } catch (error) {
    if (isReauthRequiredError(error)) {
      throw error
    }
    return undefined
  }
}

/**
 * Resolves the session's claims into the holder, once, from the token the client already holds.
 *
 * Takes the token getter rather than reaching for the `sec` bridge so the caller is inside the React
 * tree where `useAuth0` lives, which is also what makes the resolve independent of when that bridge
 * happens to be registered. Auth0 serves the token from its own cache, so the common case costs no
 * request.
 *
 * Rejects only when Auth0 says the cached session requires interactive login. A transient failure or
 * absent Auth0 provider leaves the session with no claims, which preserves the existing fail-closed
 * behavior without turning a network problem into a login loop.
 */
export const resolveSessionClaims = async (
  getAccessToken: () => Promise<string | undefined>,
): Promise<void> => {
  try {
    const claims = decodeSessionClaims(await readAccessToken(getAccessToken))
    if (claims.subject !== null) {
      userStorage.activate(claims.subject)
    }
    sessionClaimsHolder.set(claims)
  } catch (error) {
    sessionClaimsHolder.clear()
    throw error
  }
}
